import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../../firebase/config";
import { signOut } from "firebase/auth";
import { getBlogPosts } from "../../firebase/blogServices";
import { getProjects } from "../../firebase/projectServices";
import "./Admin.css";
import SpaceParticles from "../../components/Particles/SpaceParticles";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSignOutAlt,
  FaQuestionCircle,
  FaEye,
  FaCalendarAlt,
  FaUser,
} from "react-icons/fa";

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState("posts"); // 'posts' or 'projects'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch both posts and projects
        const [blogPosts, projectsList] = await Promise.all([
          getBlogPosts(),
          getProjects(),
        ]);

        setPosts(blogPosts);
        setProjects(projectsList);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Falha ao carregar os dados.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Format date to Brazilian format
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const isMobileDevice = () => {
    return window.innerWidth <= 768;
  };

  return (
    <div className="admin-page">
      <div className="admin-dashboard">
        <div className="admin-dashboard-header">
          <h1 className="admin-dashboard-title">Painel Administrativo</h1>
          <div className="admin-actions">
            <Link
              to={
                activeTab === "posts" ? "/admin/post/new" : "/admin/project/new"
              }
              className="admin-button admin-button-primary"
            >
              <FaPlus /> {activeTab === "posts" ? "Novo Post" : "Novo Projeto"}
            </Link>
            <Link
              to="/admin/help"
              className="admin-button admin-button-secondary"
            >
              <FaQuestionCircle /> Ajuda
            </Link>
            <button
              onClick={handleLogout}
              className="admin-button admin-button-secondary"
            >
              <FaSignOutAlt /> Sair
            </button>
          </div>
        </div>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === "posts" ? "active" : ""}`}
            onClick={() => setActiveTab("posts")}
          >
            Posts do Blog
          </button>
          <button
            className={`admin-tab ${activeTab === "projects" ? "active" : ""}`}
            onClick={() => setActiveTab("projects")}
          >
            Projetos
          </button>
        </div>

        {loading ? (
          <div className="admin-loading">
            <div className="loading-spinner"></div>
            <p>Carregando dados...</p>
          </div>
        ) : error ? (
          <div className="admin-error">{error}</div>
        ) : activeTab === "posts" ? (
          <div className="admin-content">
            {!isMobile ? (
              <div className="admin-posts-table-container">
                <table className="admin-posts-table">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Data</th>
                      <th>Autor</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="admin-no-posts">
                          Nenhum post encontrado. Clique em "Novo Post" para
                          criar o primeiro!
                        </td>
                      </tr>
                    ) : (
                      posts.map((post) => (
                        <tr key={post.id}>
                          <td className="admin-post-title">{post.title}</td>
                          <td>{formatDate(post.createdAt)}</td>
                          <td>{post.author || "—"}</td>
                          <td className="admin-post-actions">
                            <Link
                              to={`/admin/post/edit/${post.id}`}
                              className="admin-action-button edit"
                              title="Editar"
                            >
                              <FaEdit />
                            </Link>
                            <Link
                              to={`/blog/${post.id}`}
                              className="admin-action-button view"
                              target="_blank"
                              title="Visualizar"
                            >
                              <FaEye />
                            </Link>
                            <button
                              className="admin-action-button delete"
                              title="Excluir"
                              onClick={() =>
                                navigate(`/admin/post/delete/${post.id}`)
                              }
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-posts-card-view">
                {posts.length === 0 ? (
                  <div className="admin-no-posts">
                    Nenhum post encontrado. Clique em "Novo Post" para criar o
                    primeiro!
                  </div>
                ) : (
                  posts.map((post) => (
                    <div className="admin-post-card" key={post.id}>
                      <div className="admin-post-card-header">
                        <h3 className="admin-post-card-title">{post.title}</h3>
                      </div>
                      <div className="admin-post-card-meta">
                        <span>
                          <FaCalendarAlt /> {formatDate(post.createdAt)}
                        </span>
                        <span>
                          <FaUser /> {post.author || "—"}
                        </span>
                      </div>
                      <div className="admin-post-card-actions">
                        <Link
                          to={`/admin/post/edit/${post.id}`}
                          className="admin-action-button edit"
                          title="Editar"
                        >
                          <FaEdit /> Editar
                        </Link>
                        <Link
                          to={`/blog/${post.id}`}
                          className="admin-action-button view"
                          target="_blank"
                          title="Visualizar"
                        >
                          <FaEye /> Ver
                        </Link>
                        <button
                          className="admin-action-button delete"
                          title="Excluir"
                          onClick={() =>
                            navigate(`/admin/post/delete/${post.id}`)
                          }
                        >
                          <FaTrash /> Excluir
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="admin-content">
            <h2 className="admin-section-title">Gerenciar Projetos</h2>

            {projects.length === 0 ? (
              <div className="admin-no-items">
                <p>
                  Nenhum projeto encontrado. Clique em "Novo Projeto" para criar
                  o primeiro!
                </p>
              </div>
            ) : !isMobile ? (
              <div className="admin-posts-table-container">
                <table className="admin-posts-table">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Tipo</th>
                      <th>Link</th>
                      <th>Tags</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.id}>
                        <td>{project.title}</td>
                        <td>{project.type || "—"}</td>
                        <td>
                          {project.demoLink ? (
                            <a
                              href={project.demoLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="admin-link"
                            >
                              Ver Site
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div className="admin-tags-list">
                            {project.tags?.map((tag, idx) => (
                              <span key={idx} className="admin-tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="admin-actions-compact">
                            <Link
                              to={`/admin/project/edit/${project.id}`}
                              className="admin-action-button edit"
                              title="Editar"
                            >
                              <FaEdit />
                            </Link>
                            {project.demoLink && (
                              <a
                                href={project.demoLink}
                                className="admin-action-button view"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Visualizar"
                              >
                                <FaEye />
                              </a>
                            )}
                            <button
                              className="admin-action-button delete"
                              title="Excluir"
                              onClick={() =>
                                navigate(`/admin/project/delete/${project.id}`)
                              }
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-posts-card-view">
                {projects.map((project) => (
                  <div className="admin-post-card" key={project.id}>
                    <div className="admin-post-card-header">
                      <h3 className="admin-post-card-title">{project.title}</h3>
                    </div>
                    <div className="admin-post-card-meta">
                      <span>{project.type || "Sem tipo definido"}</span>
                    </div>
                    <div className="admin-tags-list">
                      {project.tags?.map((tag, idx) => (
                        <span key={idx} className="admin-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="admin-post-card-actions">
                      <Link
                        to={`/admin/project/edit/${project.id}`}
                        className="admin-action-button edit"
                        title="Editar"
                      >
                        <FaEdit /> Editar
                      </Link>
                      {project.demoLink && (
                        <a
                          href={project.demoLink}
                          className="admin-action-button view"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Visualizar"
                        >
                          <FaEye /> Ver
                        </a>
                      )}
                      <button
                        className="admin-action-button delete"
                        title="Excluir"
                        onClick={() =>
                          navigate(`/admin/project/delete/${project.id}`)
                        }
                      >
                        <FaTrash /> Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <SpaceParticles />
    </div>
  );
};

export default AdminDashboard;
